import { useState, useMemo, useCallback } from "react";

interface SearchableLink {
  id: string;
  title: string | null;
  url: string;
  ogMetadata?: {
    description?: string | null;
  } | null;
}

/**
 * Hook for filtering items based on search query
 * Searches through title, url, and ogMetadata.description fields
 */
export function useSearch<T extends SearchableLink>(items: T[]) {
  const [query, setQuery] = useState("");

  const filteredItems = useMemo(() => {
    if (!query.trim()) {
      return items;
    }

    const lowerQuery = query.toLowerCase().trim();

    return items.filter((item) => {
      const titleMatch = item.title?.toLowerCase().includes(lowerQuery) ?? false;
      const urlMatch = item.url.toLowerCase().includes(lowerQuery);
      const descriptionMatch = item.ogMetadata?.description
        ?.toLowerCase()
        .includes(lowerQuery) ?? false;

      return titleMatch || urlMatch || descriptionMatch;
    });
  }, [items, query]);

  const clearSearch = useCallback(() => {
    setQuery("");
  }, []);

  const hasSearchQuery = query.trim().length > 0;
  const hasNoResults = hasSearchQuery && filteredItems.length === 0;

  return {
    query,
    setQuery,
    filteredItems,
    clearSearch,
    hasSearchQuery,
    hasNoResults,
  };
}
